// app/javascript/controllers/admin_map_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "map", "loadingIndicator", "selectedInfo", "selectedContent",
    "visibleCount", "projectsCount", "topicsCount", "expertisesCount",
    "typeFilter", "continentFilter", "coordinatesDisplay",
    "infoPopup", "popupTitle", "popupContent", "viewDetailsBtn"
  ]
  
  static values = {
    mapboxToken: String,
    operationalAreasUrl: String,
    adminsCountriesUrl: String,
    adminsRegionsUrl: String,
    countriesLookupUrl: String
  }

  connect() {
    if (typeof mapboxgl === 'undefined') {
      this.showError('Mapbox GL JS n\'est pas chargé')
      return
    }

    if (!this.mapboxTokenValue) {
      this.showError('Token Mapbox manquant')
      return
    }

    if (!mapboxgl.supported()) {
      this.showError('La carte interactive n\'est pas disponible sur ce navigateur.')
      return
    }

    this.initializeMap()
  }

  initializeMap() {
    mapboxgl.accessToken = this.mapboxTokenValue
    try { mapboxgl.config.EVENTS_URL = '' } catch (e) { /* read-only */ }

    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [2.3522, 48.8566], // Paris par défaut
      zoom: 2,
      collectResourceTiming: false,
      projection: 'globe'
    })

    // Ajouter les contrôles
    this.map.addControl(new mapboxgl.NavigationControl(), 'top-right')
    this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right')

    // Événements de la carte
    this.map.on('load', () => {
      this.onMapLoad()
    })

    this.map.on('mousemove', (e) => {
      this.updateCoordinatesDisplay(e.lngLat.lat, e.lngLat.lng)
    })

    this.map.on('click', (e) => {
      this.onMapClick(e)
    })

    // Initialiser les données
    this.operationalAreas = []
    this.filteredAreas = []
    this.countriesData = null
    this.regionsData = null
    this.selectedFeature = null
    this.clustering = false
  }

  onMapLoad() {
    this.loadRegionsLayer()
    this.loadCountriesLayer()
    this.loadOperationalAreas()
    this.hideLoadingIndicator()
  }

  async loadRegionsLayer() {
    try {
      // Charger les données des régions/continents (Admin1)
      const response = await fetch(`${this.adminsRegionsUrlValue}?simplify=true&precision=2`)
      this.regionsData = await response.json()

      // Ajouter la source des régions
      this.map.addSource('regions', {
        type: 'geojson',
        data: this.regionsData
      })

      // Ajouter les couches de rendu des régions avec une couleur différente
      this.map.addLayer({
        id: 'regions-fill',
        type: 'fill',
        source: 'regions',
        paint: {
          'fill-color': 'rgba(59, 130, 246, 0.1)', // Bleu clair pour les régions
          'fill-opacity': 0.2
        }
      })

      this.map.addLayer({
        id: 'regions-line',
        type: 'line',
        source: 'regions',
        paint: {
          'line-color': '#3B82F6', // Bleu pour les frontières des régions
          'line-width': 2,
          'line-opacity': 0.8
        }
      })

      // Événements pour les régions
      this.map.on('click', 'regions-fill', (e) => {
        this.onRegionClick(e)
      })

      this.map.on('mouseenter', 'regions-fill', () => {
        this.map.getCanvas().style.cursor = 'pointer'
      })

      this.map.on('mouseleave', 'regions-fill', () => {
        this.map.getCanvas().style.cursor = ''
      })

    } catch (error) {
    }
  }

  async loadCountriesLayer() {
    try {
      const response = await fetch(`${this.adminsCountriesUrlValue}?simplify=true&precision=2`)
      this.countriesData = await response.json()

      // Ajouter la source des pays
      this.map.addSource('countries', {
        type: 'geojson',
        data: this.countriesData
      })

      // Ajouter les couches de rendu des pays avec une couleur différente des régions
      this.map.addLayer({
        id: 'countries-fill',
        type: 'fill',
        source: 'countries',
        paint: {
          'fill-color': 'rgba(34, 197, 94, 0.05)', // Vert très clair pour les pays
          'fill-opacity': 0.1
        }
      })

      this.map.addLayer({
        id: 'countries-line',
        type: 'line',
        source: 'countries',
        paint: {
          'line-color': '#22C55E', // Vert pour les frontières des pays
          'line-width': 1,
          'line-opacity': 0.6
        }
      })

      // Événements pour les pays
      this.map.on('click', 'countries-fill', (e) => {
        this.onCountryClick(e)
      })

      this.map.on('mouseenter', 'countries-fill', () => {
        this.map.getCanvas().style.cursor = 'pointer'
      })

      this.map.on('mouseleave', 'countries-fill', () => {
        this.map.getCanvas().style.cursor = ''
      })

    } catch (error) {
    }
  }

  async loadOperationalAreas() {
    try {
      this.showLoadingIndicator()
      
      const response = await fetch(this.operationalAreasUrlValue)
      const data = await response.json()
      
      this.operationalAreas = data.features || []
      this.filteredAreas = [...this.operationalAreas]
      
      this.updateOperationalAreasLayer()
      this.updateStatistics()
      
    } catch (error) {
      this.showError('Erreur lors du chargement des zones opérationnelles')
    } finally {
      this.hideLoadingIndicator()
    }
  }

  updateOperationalAreasLayer() {
    // Vérifier l'existence des couches avant de les supprimer
    this.removeOperationalAreasLayers()

    if (this.filteredAreas.length === 0) return

    // Créer les données GeoJSON avec clustering si activé
    const geojsonData = {
      type: 'FeatureCollection',
      features: this.filteredAreas
    }

    // S'assurer que la source n'existe pas déjà
    if (this.map.getSource('operational-areas')) {
      this.map.getSource('operational-areas').setData(geojsonData)
    } else {
      this.map.addSource('operational-areas', {
        type: 'geojson',
        data: geojsonData,
        cluster: this.clustering,
        clusterMaxZoom: 14,
        clusterRadius: 50
      })
    }

    if (this.clustering) {
      this.addClusteredLayers()
    } else {
      this.addPointLayers()
    }
  }

  // Supprimer toutes les couches des zones opérationnelles
  removeOperationalAreasLayers() {
    const layersToRemove = ['operational-areas', 'clusters', 'cluster-count']
    
    layersToRemove.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId)
      }
    })

    // Supprimer la source si elle existe et qu'aucune couche ne l'utilise
    if (this.map.getSource('operational-areas')) {
      const hasLayersUsingSource = layersToRemove.some(layerId => this.map.getLayer(layerId))
      if (!hasLayersUsingSource) {
        this.map.removeSource('operational-areas')
      }
    }
  }

  addClusteredLayers() {
    // Clusters
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'operational-areas',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#3B82F6',
          100,
          '#1D4ED8',
          750,
          '#1E40AF'
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          15,
          100,
          20,
          750,
          25
        ]
      }
    })

    // Nombre dans les clusters
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'operational-areas',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#ffffff'
      }
    })

    // Points individuels
    this.addPointLayers()

    // Événements pour les clusters
    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      })
      const clusterId = features[0].properties.cluster_id
      this.map.getSource('operational-areas').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return

          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          })
        }
      )
    })
  }

  addPointLayers() {
    // Utiliser un filtre valide pour les points individuels
    const pointFilter = this.clustering ? ['!', ['has', 'point_count']] : ['==', ['geometry-type'], 'Point']

    // Points individuels avec couleurs par type
    this.map.addLayer({
      id: 'operational-areas',
      type: 'circle',
      source: 'operational-areas',
      filter: pointFilter,
      paint: {
        'circle-color': [
          'match',
          ['get', 'operationalable_type'],
          'Project', '#3B82F6',
          'Topic', '#10B981',
          'Expertise', '#8B5CF6',
          '#6B7280'
        ],
        'circle-radius': 8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    })

    // Événements pour les points
    this.map.on('click', 'operational-areas', (e) => {
      this.onOperationalAreaClick(e)
    })

    this.map.on('mouseenter', 'operational-areas', () => {
      this.map.getCanvas().style.cursor = 'pointer'
    })

    this.map.on('mouseleave', 'operational-areas', () => {
      this.map.getCanvas().style.cursor = ''
    })
  }

  onOperationalAreaClick(e) {
    const feature = e.features[0]
    const props = feature.properties
    
    this.selectedFeature = feature
    this.showOperationalAreaPopup(props, e.lngLat)
  }

  onRegionClick(e) {
    const feature = e.features[0]
    const props = feature.properties
    
    this.showRegionPopup(props, e.lngLat)
  }

  onCountryClick(e) {
    const feature = e.features[0]
    const props = feature.properties
    
    this.showCountryPopup(props, e.lngLat)
  }

  async onMapClick(e) {
    // Recherche inverse pour obtenir des informations sur le pays
    try {
      const response = await fetch(this.countriesLookupUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          latitude: e.lngLat.lat,
          longitude: e.lngLat.lng
        })
      })
      
      if (response.ok) {
        const data = await response.json()
      }
    } catch (error) {
    }
  }

  showOperationalAreaPopup(props, lngLat) {
    const popup = new mapboxgl.Popup({ offset: 25 })
      .setLngLat(lngLat)
      .setHTML(`
        <div class="p-3">
          <h3 class="font-bold text-sm mb-2">${props.name}</h3>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-gray-600">Type:</span>
              <span class="font-medium">${props.operationalable_type}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Créé par:</span>
              <span class="font-medium">${props.created_by}</span>
            </div>
          </div>
          <div class="mt-3">
            <button class="w-full px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                    onclick="window.location.href='/arko_core/${props.operationalable_type.toLowerCase()}s/${props.operationalable_id}'">
              Voir les détails
            </button>
          </div>
        </div>
      `)
      .addTo(this.map)
  }

  showRegionPopup(props, lngLat) {
    const popup = new mapboxgl.Popup({ offset: 25 })
      .setLngLat(lngLat)
      .setHTML(`
        <div class="p-3">
          <h3 class="font-bold text-sm mb-2 text-blue-800">${props.name || props.continent}</h3>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-gray-600">Type:</span>
              <span class="font-medium text-blue-700">Région/Continent</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Niveau:</span>
              <span class="font-medium">Admin1</span>
            </div>
          </div>
        </div>
      `)
      .addTo(this.map)
  }

  showCountryPopup(props, lngLat) {
    const popup = new mapboxgl.Popup({ offset: 25 })
      .setLngLat(lngLat)
      .setHTML(`
        <div class="p-3">
          <h3 class="font-bold text-sm mb-2 text-green-800">${props.name_long || props.name}</h3>
          <div class="space-y-1 text-xs">
            <div class="flex justify-between">
              <span class="text-gray-600">Type:</span>
              <span class="font-medium text-green-700">Pays</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Continent:</span>
              <span class="font-medium">${props.continent}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Code ISO:</span>
              <span class="font-medium">${props.iso_a3}</span>
            </div>
            ${props.pop_est ? `
              <div class="flex justify-between">
                <span class="text-gray-600">Population:</span>
                <span class="font-medium">${this.formatNumber(props.pop_est)}</span>
              </div>
            ` : ''}
          </div>
        </div>
      `)
      .addTo(this.map)
  }

  // Méthodes de contrôle
  toggleCountries(event) {
    const visible = event.target.checked
    const visibility = visible ? 'visible' : 'none'
    
    if (this.map.getLayer('countries-line')) {
      this.map.setLayoutProperty('countries-line', 'visibility', visibility)
    }
    if (this.map.getLayer('countries-fill')) {
      this.map.setLayoutProperty('countries-fill', 'visibility', visibility)
    }
  }

  toggleRegions(event) {
    const visible = event.target.checked
    const visibility = visible ? 'visible' : 'none'
    
    if (this.map.getLayer('regions-line')) {
      this.map.setLayoutProperty('regions-line', 'visibility', visibility)
    }
    if (this.map.getLayer('regions-fill')) {
      this.map.setLayoutProperty('regions-fill', 'visibility', visibility)
    }
  }

  toggleOperationalAreas(event) {
    const visible = event.target.checked
    const visibility = visible ? 'visible' : 'none'
    const layers = ['operational-areas', 'clusters', 'cluster-count']
    
    layers.forEach(layer => {
      if (this.map.getLayer(layer)) {
        this.map.setLayoutProperty(layer, 'visibility', visibility)
      }
    })
  }

  toggleClustering(event) {
    this.clustering = event.target.checked
    this.updateOperationalAreasLayer()
  }

  filterByType(event) {
    this.applyFilters()
  }

  filterByContinent(event) {
    this.applyFilters()
  }

  applyFilters() {
    const typeFilter = this.typeFilterTarget.value
    const continentFilter = this.continentFilterTarget.value

    this.filteredAreas = this.operationalAreas.filter(area => {
      const props = area.properties
      
      if (typeFilter && props.operationalable_type !== typeFilter) {
        return false
      }
      
      // Pour le filtre continent, on devrait idéalement avoir cette info dans les props
      // ou faire une requête pour l'obtenir
      
      return true
    })

    this.updateOperationalAreasLayer()
    this.updateStatistics()
  }

  resetFilters() {
    this.typeFilterTarget.value = ''
    this.continentFilterTarget.value = ''
    this.applyFilters()
  }

  // Contrôles de navigation
  zoomIn() {
    this.map.zoomIn()
  }

  zoomOut() {
    this.map.zoomOut()
  }

  resetView() {
    this.map.easeTo({
      center: [2.3522, 48.8566],
      zoom: 2
    })
  }

  // Méthodes utilitaires
  updateStatistics() {
    const stats = this.calculateStatistics()
    
    this.visibleCountTarget.textContent = stats.total
    this.projectsCountTarget.textContent = stats.projects
    this.topicsCountTarget.textContent = stats.topics
    this.expertisesCountTarget.textContent = stats.expertises
  }

  calculateStatistics() {
    const stats = {
      total: this.filteredAreas.length,
      projects: 0,
      topics: 0,
      expertises: 0
    }

    this.filteredAreas.forEach(area => {
      const type = area.properties.operationalable_type
      switch (type) {
        case 'Project':
          stats.projects++
          break
        case 'Topic':
          stats.topics++
          break
        case 'Expertise':
          stats.expertises++
          break
      }
    })

    return stats
  }

  updateCoordinatesDisplay(lat, lng) {
    if (this.hasCoordinatesDisplayTarget) {
      this.coordinatesDisplayTarget.textContent = 
        `Latitude: ${lat.toFixed(4)} | Longitude: ${lng.toFixed(4)}`
    }
  }

  showLoadingIndicator() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.style.display = 'block'
    }
  }

  hideLoadingIndicator() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.style.display = 'none'
    }
  }

  showError(message) {
    // Vous pouvez ajouter une notification toast ici
  }

  formatNumber(num) {
    if (!num) return 'N/A'
    return new Intl.NumberFormat('fr-FR').format(num)
  }

  closePopup() {
    if (this.hasInfoPopupTarget) {
      this.infoPopupTarget.style.display = 'none'
    }
  }

  viewDetails() {
    if (this.selectedFeature) {
      const props = this.selectedFeature.properties
      window.location.href = `/arko_core/${props.operationalable_type.toLowerCase()}s/${props.operationalable_id}`
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
    }
  }
}